import useFilteringSettings from "./useFilteringSettings";
import InputComponent from "V2.0/components/InputComponent";
import { Switch } from "V2.0/common/switch";
import { Button } from "V2.0/common/button";
import { useMessagingSettings } from "../useMessagingSettings";

export default function FilteringSettings(props) {
  const {
    locked,
    workspaceId,
    assistantId,
    syncAssistant,
    messagingSettings,
    // Instagram-only: when true, render the story reply / story mention toggles in this panel.
    showStoryToggles,
  } = props;

  const {
    filteringSettingsState,
    updateFilteringPrompt,
    toggleFilteringEnable,
    toggleStorySetting,
  } = useFilteringSettings(
    messagingSettings?.filteringSettings || {
      messageFilteringEnabled: false,
      filteringPrompt: "",
    }
  );

  const { updateMessagingSettings } = useMessagingSettings({
    syncAssistant,
    workspaceId,
    assistantId,
  });

  function handleSave() {
    const payload = {
      filteringSettings: {
        messageFilteringEnabled: filteringSettingsState.messageFilteringEnabled,
        filteringPrompt: filteringSettingsState.filteringPrompt,
        // Story toggles are Instagram-only; only persist them where the panel shows them so other channels
        // don't write these fields. The backend deep-merges messagingSettings, so omitting them is safe.
        ...(showStoryToggles && {
          enableStoryReply: filteringSettingsState.enableStoryReply,
          enableStoryMention: filteringSettingsState.enableStoryMention,
        }),
      },
    };

    updateMessagingSettings(payload);
  }

  return (
    <div className="flex flex-col border rounded-lg w-3/5 min-w-[600px] m-4">
      <div className="p-4 bg-gray-50 rounded-t-lg px-6">
        <p className="text-base font-semibold text-gray-900">
          Response Filters
        </p>
        <p className="text-sm mt-1 text-gray-500">
          Configure when the assistant should skip responding to a message
        </p>
      </div>
      <div className="grid grid-cols-4 p-4 px-8 w-full space-y-8">
        <div className="col-span-4 flex gap-x-2">
          <p className="text-sm font-semibold">Use Response Filtering</p>
          <Switch
            color="emerald"
            checked={filteringSettingsState.messageFilteringEnabled}
            onClick={toggleFilteringEnable}
            disabled={locked}
          />
        </div>
        <div className="col-span-4 w-full">
          <InputComponent
            type="textArea"
            style={{ height: "150px", fontSize: "13px" }}
            label="Filter Conditions *"
            description="Define the filter conditions to ignore a message"
            value={filteringSettingsState.filteringPrompt}
            onChange={(e) => updateFilteringPrompt(e.target.value)}
            disabled={locked || !filteringSettingsState.messageFilteringEnabled}
          />
        </div>
        {showStoryToggles && (
          <>
            <div className="col-span-4 border-t border-gray-200" />
            <div className="col-span-4 flex flex-col gap-y-1">
              <div className="flex gap-x-2">
                <p className="text-sm font-semibold">Reply to story replies</p>
                <Switch
                  color="emerald"
                  checked={filteringSettingsState.enableStoryReply}
                  onClick={() => toggleStorySetting("enableStoryReply")}
                  disabled={locked}
                />
              </div>
              <p className="text-xs text-gray-500">
                When off, the assistant won't respond when someone replies to
                your Instagram story.
              </p>
            </div>
            <div className="col-span-4 flex flex-col gap-y-1">
              <div className="flex gap-x-2">
                <p className="text-sm font-semibold">Reply to story mentions</p>
                <Switch
                  color="emerald"
                  checked={filteringSettingsState.enableStoryMention}
                  onClick={() => toggleStorySetting("enableStoryMention")}
                  disabled={locked}
                />
              </div>
              <p className="text-xs text-gray-500">
                When off, the assistant won't respond when someone mentions you
                in their Instagram story.
              </p>
            </div>
          </>
        )}
      </div>
      <div className="bg-gray-50 rounded-b-lg px-4 py-3 text-right">
        {!locked && <Button onClick={handleSave}>Save</Button>}
      </div>
    </div>
  );
}
